/**
 * O'Copro - Menu Component
 * Drawer latéral avec toutes les sections (V6)
 * ✅ NOUVEAU: Masquage conditionnel Messagerie et Demandes selon chatEnabled/requestsEnabled
 * ✅ NOUVEAU: Lien "Portail copropriétaire" pour owners/council si portalUrl renseigné
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  User,
  Info,
  Calendar,
  FileText,
  MessageSquare,
  Vote,
  Bell,
  Settings,
  Users,
  UserPlus,
  UserCheck,
  ClipboardCheck,
  FolderOpen,
  Building2,
  Briefcase,
  HelpCircle,
  LogOut,
  ChevronRight,
  ExternalLink
} from 'lucide-react';
import { useAuthStore, useUIStore, useResidenceStore } from '@/stores';
import { usePermissions } from '@/hooks';
import { USER_CATEGORIES } from '@/config/constants';

// ============================================================================
// TYPES
// ============================================================================

interface MenuProps {
  isOpen?: boolean;
  onClose?: () => void;
}

interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  badge?: number;
  disabled?: boolean;
}

interface MenuSectionProps {
  title: string;
  children: React.ReactNode;
}

// ============================================================================
// SOUS-COMPOSANTS
// ============================================================================

const MenuItem: React.FC<MenuItemProps> = ({ icon, label, onClick, badge, disabled = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    <div className="flex items-center gap-3">
      <span className="text-gray-500">{icon}</span>
      <span className="text-gray-700 font-medium">{label}</span>
    </div>
    <div className="flex items-center gap-2">
      {badge !== undefined && badge > 0 && (
        <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
      <ChevronRight size={18} className="text-gray-400" />
    </div>
  </button>
);

const MenuSection: React.FC<MenuSectionProps> = ({ title, children }) => (
  <div className="py-2">
    <h3 className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">{title}</h3>
    <div className="space-y-1">{children}</div>
  </div>
);

const Divider: React.FC = () => <div className="border-t border-gray-200 my-2" />;

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

export const Menu: React.FC<MenuProps> = ({ isOpen: isOpenProp, onClose: onCloseProp }) => {
  const navigate = useNavigate();
  
  // Utiliser les props si fournies, sinon le store
  const isMenuOpenStore = useUIStore(state => state.isMenuOpen);
  const closeMenuStore = useUIStore(state => state.closeMenu);
  
  const isOpen = isOpenProp !== undefined ? isOpenProp : isMenuOpenStore;
  const closeMenu = onCloseProp || closeMenuStore;
  
  const currentUser = useAuthStore(state => state.currentUser);
  const logout = useAuthStore(state => state.logout);
  
  const {
    canManage, canValidateRequests, canManageDocuments, canManageUsers,
    canValidateInvitations, canEditResidenceSettings, canManagePolls,
    canSendNotifications, isAdmin, isCouncil
  } = usePermissions();

  const canManageBailleurs = isAdmin || isCouncil;

  // ✅ NOUVEAU: Accès au portail copropriétaire du syndic
  const currentResidence = useResidenceStore(state => state.currentResidence);
  const portalUrl = (currentResidence as any)?.syndic?.portalUrl || '';

  // ✅ NOUVEAU: Restrictions d'accès aux fonctionnalités (true par défaut)
  const chatEnabled = currentUser?.chatEnabled !== false;
  const requestsEnabled = currentUser?.requestsEnabled !== false;

  // Badge "Inscriptions" — valeur globale gérée par usePendingBadge (App.tsx)
  const inscriptionsPending = useUIStore(state => state.pendingBadgeCount);

  const isOwnerOrCouncil = ['owner', 'council'].includes(currentUser?.category || '');
  const showPortalLink = !!(portalUrl && isOwnerOrCouncil);

  const handleNavigate = (path: string) => { 
    closeMenu(); 
    navigate(path); 
  };

  // ✅ NOUVEAU: Ouvrir le portail copropriétaire dans un nouvel onglet
  const handleOpenPortal = () => {
    closeMenu();
    const url = portalUrl.startsWith('http') ? portalUrl : `https://${portalUrl}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  
  const handleLogout = async () => { 
    closeMenu(); 
    await logout(); 
    navigate('/login'); 
  };

  if (!isOpen) return null;

  const category = currentUser?.category || 'tenant';
  const categoryInfo = USER_CATEGORIES[category] || { label: 'Résident', color: 'bg-gray-500' };

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-50 transition-opacity" onClick={closeMenu} />
      
      {/* Drawer */}
      <div className="fixed inset-y-0 left-0 w-80 max-w-[85%] bg-white z-50 shadow-xl flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 px-4 pb-4 pt-[calc(1rem+env(safe-area-inset-top))]">
          <button
            onClick={closeMenu}
            className="absolute top-[calc(1rem+env(safe-area-inset-top))] right-4 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
          >
            <X size={18} />
          </button>
          
          <button onClick={() => handleNavigate('/profile')} className="flex items-center gap-3 mt-4 w-full text-left">
            <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-white text-xl font-bold">
              {currentUser?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="flex-1">
              <h2 className="text-white font-semibold text-lg truncate">{currentUser?.name || 'Utilisateur'}</h2>
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">{categoryInfo.label}</span>
                {currentUser?.apartment && <span className="text-white/70 text-sm">Apt {currentUser.apartment}</span>}
              </div>
            </div>
          </button>
        </div>

        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto">
          <MenuSection title="Mon compte">
            <MenuItem icon={<User size={20} />} label="Mon Profil" onClick={() => handleNavigate('/profile')} />
          </MenuSection>
          
          <Divider />
          
          <MenuSection title="Navigation">
            <MenuItem icon={<Info size={20} />} label="Informations" onClick={() => handleNavigate('/info')} />
            <MenuItem icon={<Calendar size={20} />} label="Calendrier" onClick={() => handleNavigate('/calendar')} />
            {/* ✅ Demandes - masqué si requestsEnabled = false */}
            {requestsEnabled && (
              <MenuItem icon={<FileText size={20} />} label="Demandes" onClick={() => handleNavigate('/requests')} />
            )}
            <MenuItem icon={<FolderOpen size={20} />} label="Documents" onClick={() => handleNavigate('/documents')} />
            <MenuItem icon={<Vote size={20} />} label="Sondages" onClick={() => handleNavigate('/polls')} />
            {/* ✅ Messagerie - masqué si chatEnabled = false */}
            {chatEnabled && (
              <MenuItem icon={<MessageSquare size={20} />} label="Messagerie" onClick={() => handleNavigate('/chat')} />
            )}
            {/* ✅ NOUVEAU: Portail copropriétaire — visible pour owners et council si portalUrl renseigné */}
            {showPortalLink && (
              <MenuItem icon={<ExternalLink size={20} />} label="Portail copropriétaire" onClick={handleOpenPortal} />
            )}
            <MenuItem icon={<UserCheck size={20} />} label="Confirmer un nouvel arrivant" onClick={() => handleNavigate('/confirm-newcomer')} />
          </MenuSection>
          
          <Divider />
          
          {canManage && (
            <>
              <MenuSection title="Gestion">
                {canValidateRequests && <MenuItem icon={<ClipboardCheck size={20} />} label="Validations" onClick={() => handleNavigate('/validations')} />}
                {canManageDocuments && <MenuItem icon={<FolderOpen size={20} />} label="Gérer documents" onClick={() => handleNavigate('/manage-documents')} />}
                {canManagePolls && <MenuItem icon={<Vote size={20} />} label="Gérer sondages" onClick={() => handleNavigate('/manage-polls')} />}
                {canManageUsers && <MenuItem icon={<Users size={20} />} label="Utilisateurs" onClick={() => handleNavigate('/users')} />}
                {canValidateInvitations && <MenuItem icon={<UserPlus size={20} />} label="Inscriptions" onClick={() => handleNavigate('/pending-users')} badge={inscriptionsPending > 0 ? inscriptionsPending : undefined} />}
                {canSendNotifications && <MenuItem icon={<Bell size={20} />} label="Envoyer une notification" onClick={() => handleNavigate('/send-notification')} />}
              </MenuSection>
              <Divider />
            </>
          )}
          
          <MenuSection title="Paramètres">
            {canEditResidenceSettings && <MenuItem icon={<Building2 size={20} />} label="Paramètres résidence" onClick={() => handleNavigate('/residence-settings')} />}
            {canManageBailleurs && <MenuItem icon={<Briefcase size={20} />} label="Bailleur Social" onClick={() => handleNavigate('/bailleurs')} />}
            {canEditResidenceSettings && <MenuItem icon={<Settings size={20} />} label="Services" onClick={() => handleNavigate('/services')} />}
            <MenuItem icon={<Bell size={20} />} label="Préférences notifications" onClick={() => handleNavigate('/notification-settings')} />
          </MenuSection>
          
          <Divider />
          
          <MenuSection title="Aide">
            <MenuItem icon={<HelpCircle size={20} />} label="Support" onClick={() => handleNavigate('/support')} />
          </MenuSection>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4">
          <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg font-medium">
            <LogOut size={20} />
            <span>Déconnexion</span>
          </button>
        </div>
        
        <div className="text-center py-2 text-xs text-gray-400 safe-area-bottom">O'Copro v1.0.0</div>
      </div>
    </>
  );
};

export default Menu;
